import { resolveProductPrice } from "./productPrice";

/**
 * add_to_cart / remove_from_cart describe a cart change the server confirmed.
 *
 * Callers snapshot the product (or cart line) when the shopper acts, await the
 * cart store action, and only then ask these helpers whether that exact
 * operation succeeded. Nothing here reads live component state after the
 * request, so an SPA navigation to another product or a button that unmounts
 * while the request is pending can neither change nor drop the event.
 */

export type CartAnalyticsProduct = Readonly<{
  id: string | number;
  sku?: string;
  name?: string;
  price?: unknown;
  special?: unknown;
}>;

export type CartAnalyticsLine = Readonly<{
  key: string;
  quantity: number;
  product: CartAnalyticsProduct;
}>;

type CartActionResult =
  | {
      ok?: boolean;
      removed?: boolean;
      cart?: { products?: unknown } | null;
    }
  | null
  | undefined;

const hasValue = (value: unknown) =>
  value !== null && value !== undefined && String(value).trim() !== "";

export const snapshotCartAnalyticsProduct = (
  product: any
): CartAnalyticsProduct | null => {
  if (!product || !hasValue(product.id)) return null;
  return Object.freeze({
    id: product.id,
    sku: product.sku,
    name: product.name,
    price: product.price,
    special: product.special,
  });
};

export const snapshotCartAnalyticsLine = (
  line: any
): CartAnalyticsLine | null => {
  const product = snapshotCartAnalyticsProduct(line?.product);
  if (!product || !hasValue(line?.key)) return null;
  return Object.freeze({
    key: String(line.key),
    quantity: Number(line.quantity) || 1,
    product,
  });
};

/**
 * Units confirmed by a `store/cart/add` result for the requested product, or 0
 * when the operation failed or the returned cart does not contain the product.
 */
export const confirmedAddedQuantity = (
  result: CartActionResult,
  productId: string | number,
  requestedQuantity: number
): number => {
  if (result?.ok !== true) return 0;
  const products = result.cart?.products;
  if (!Array.isArray(products)) return 0;
  const inCart = products.some(
    (line: any) => String(line?.product?.id) === String(productId)
  );
  const quantity = Number(requestedQuantity);
  return inCart && Number.isInteger(quantity) && quantity > 0 ? quantity : 0;
};

/**
 * True only when `store/cart/remove` reports that this call removed the line
 * (a repeated request for an already removed key reports `removed: false`).
 */
export const isConfirmedCartRemoval = (
  result: CartActionResult,
  key: string
): boolean => {
  if (result?.ok !== true || result.removed !== true) return false;
  const products = result.cart?.products;
  return (
    Array.isArray(products) &&
    !products.some((line: any) => String(line?.key) === String(key))
  );
};

const parseNumber = (value: unknown): number => {
  const n = Number(String(value ?? "").replace(/[^0-9.\-]+/g, ""));
  return isNaN(n) ? 0 : n;
};

/** GA4 remove_from_cart for a confirmed removal (payload shape unchanged). */
export const trackRemoveFromCart = (line: CartAnalyticsLine): boolean => {
  if (typeof window === "undefined") return false;
  const dataLayer = (window as any).dataLayer;
  if (!dataLayer) return false;
  const { product, quantity } = line;
  const price = parseNumber(
    resolveProductPrice(product.special, product.price)
  );
  const value = (Number(price) || 0) * quantity;
  dataLayer.push({ ecommerce: null });
  dataLayer.push({
    event: "remove_from_cart",
    ecommerce: {
      currency: "USD",
      value,
      items: [
        {
          item_id: product.id,
          sku: product.sku,
          item_name: product.name,
          price,
          quantity,
        },
      ],
    },
  });
  return true;
};
